import React from "react";
import { Helmet } from "react-helmet-async";
import { SEO_CONFIG } from "../utils/seo";

/**
 * Dynamic SEO Component that uses environment variables
 * This component handles meta tags that need to be dynamic based on .env configuration
 */
const DynamicSEO = ({
  title,
  description,
  url,
  image,
  type = "website",
  noindex = false
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || SEO_CONFIG.siteUrl;
  const siteName = import.meta.env.VITE_SITE_NAME || SEO_CONFIG.siteName;
  const twitterHandle = import.meta.env.VITE_TWITTER_HANDLE || SEO_CONFIG.twitterHandle;
  const facebookPage = import.meta.env.VITE_FACEBOOK_PAGE || SEO_CONFIG.facebookPage;
  const siteDescription = import.meta.env.VITE_SITE_DESCRIPTION || SEO_CONFIG.defaultDescription;

  const fullTitle = title ? `${title} | ${siteName}` : `${SEO_CONFIG.defaultTitle}`;
  const metaDescription = description || siteDescription;
  const canonicalUrl = url ? `${siteUrl}${url}` : siteUrl;
  const ogImage = image ? `${siteUrl}${image}` : `${siteUrl}/images/og-default.jpg`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots Meta */}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"}
      />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${siteName} - Educational Resources`} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${siteName} Educational Resources`} />

      {/* Additional Meta Tags */}
      <meta name="application-name" content={siteName} />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Language and Regional */}
      <link rel="alternate" hreflang="en" href={canonicalUrl} />
      <link rel="alternate" hreflang="x-default" href={canonicalUrl} />

      {/* RSS Feed */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title={`${siteName} RSS Feed`}
        href={`${siteUrl}/rss.xml`}
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": siteName,
          "description": siteDescription,
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "sameAs": [
            facebookPage,
            `https://twitter.com/${twitterHandle.replace('@', '')}`
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+977-9849826321",
            "contactType": "customer support",
            "email": import.meta.env.VITE_CONTACT_EMAIL || "contact@webdoko.com",
            "availableLanguage": ["English", "Nepali"]
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Baneshwor-Sankhamul Rd.",
            "addressLocality": "Kathmandu",
            "addressCountry": "Nepal"
          }
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "url": siteUrl,
          "description": siteDescription,
          "inLanguage": "en",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteUrl}/search?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          },
          "publisher": {
            "@type": "Organization",
            "name": siteName,
            "logo": {
              "@type": "ImageObject",
              "url": `${siteUrl}/logo.png`
            }
          }
        })}
      </script>

      {/* Search Console and Webmaster Verification */}
      {import.meta.env.VITE_GOOGLE_SITE_VERIFICATION && (
        <meta
          name="google-site-verification"
          content={import.meta.env.VITE_GOOGLE_SITE_VERIFICATION}
        />
      )}
      {import.meta.env.VITE_BING_WEBMASTER_VERIFICATION && (
        <meta
          name="msvalidate.01"
          content={import.meta.env.VITE_BING_WEBMASTER_VERIFICATION}
        />
      )}
      {import.meta.env.VITE_YANDEX_VERIFICATION && (
        <meta
          name="yandex-verification"
          content={import.meta.env.VITE_YANDEX_VERIFICATION}
        />
      )}
    </Helmet>
  );
};

export default DynamicSEO;
